import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  FEEDBACK_CATEGORIES, useFeedbackListApi, useEmployeeNames, buildFeedbackColumns,
} from './config';
import GenericSelect from '../../components/ui/GenericSelect';
import DataTable from '../../components/ui/DataTable';
import { parseTableParams } from '../../utils/Utils';

// Task 31 — Sürekli Geri Bildirim: yönetici "ekibim" görünümü. Backend
// (GetTeamAsync) TeamFeedbackVisibleToManager flag'i kapalıyken 409
// performance.feedback.teamViewDisabled döner — bu skipErrorToast ile
// bastırılıp (config'teki useFeedbackListApi) sayfa içi bilgi boş-durumuna
// çevrilir, hata toast'ı YOK (brief: "teamViewDisabled 409 → skipErrorToast +
// bilgi boş-durumu").
const Container = styled.div``;

const FilterRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  max-width: 280px;
`;

const EmptyBox = styled.div`
  padding: 40px 18px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
`;

export default function TeamFeedbackPage() {
  const { t, i18n } = useTranslation();

  const [categoryFilter, setCategoryFilter] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const categoryOptions = useMemo(() => FEEDBACK_CATEGORIES.map((c) => ({
    id: c,
    name: t(`performance.feedback.category.${c}`),
  })), [t]);

  const requestParams = useMemo(() => ({
    ...queryParams,
    ...(categoryFilter ? { 'category[eq]': categoryFilter } : {}),
  }), [queryParams, categoryFilter]);

  const {
    data, fetching, pagination, disabled,
  } = useFeedbackListApi('team', requestParams);

  const receiverIds = useMemo(() => data.map((r) => r.receiverEmployeeId), [data]);
  const names = useEmployeeNames(receiverIds);

  const baseColumns = useMemo(
    () => buildFeedbackColumns(t, data, names, i18n.language),
    [t, data, names, i18n.language],
  );

  const handleDataStateChange = useCallback((_action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((fl) => setFilterList(fl), []);

  return (
    <Container>
      {!disabled && (
        <FilterRow>
          <GenericSelect
            label={t('performance.feedback.categoryLabel')}
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={categoryOptions}
            idKey="id"
            labelKey="name"
          />
        </FilterRow>
      )}

      {disabled ? (
        <EmptyBox>{t('responseErrors.performance.feedback.teamViewDisabled')}</EmptyBox>
      ) : (
        <DataTable
          title={t('performance.feedback.teamTitle')}
          fetching={fetching}
          columns={baseColumns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          disableFilters
        />
      )}
    </Container>
  );
}
